import {
  MAX_RECENT_RUNS,
  PROGRESS_SCHEMA_VERSION,
  createPlayerProgress,
  normalizeNickname,
  weaponsForClogs,
  type LevelRecord,
  type PlayerProgress,
  type RecentRun,
} from "./progression";

const STORAGE_KEY = "buttroids.preferences.v1";
const PROGRESS_KEY = "buttroids.progress.v2";

export interface Preferences {
  bestScore: number;
  tutorialComplete: boolean;
  music: boolean;
  effects: boolean;
  haptics: boolean;
  muteAll: boolean;
  reducedMotion: boolean;
  sensitivity: number;
  tiltSensitivity: number;
  tiltEnabled: boolean;
  tiltCenter: number;
}

export const DEFAULT_PREFERENCES: Preferences = {
  bestScore: 0,
  tutorialComplete: false,
  music: true,
  effects: true,
  haptics: true,
  muteAll: false,
  reducedMotion: false,
  sensitivity: 1,
  tiltSensitivity: 1,
  tiltEnabled: false,
  tiltCenter: 0,
};

function availableStorage(): Pick<Storage, "getItem" | "setItem"> | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    // Some privacy modes reject access to the localStorage property itself.
    return null;
  }
}

function sanitize(raw: unknown): Preferences {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_PREFERENCES };
  const value = raw as Partial<Preferences>;
  const bool = (candidate: unknown, fallback: boolean): boolean =>
    typeof candidate === "boolean" ? candidate : fallback;
  const finite = (candidate: unknown, fallback: number): number =>
    typeof candidate === "number" && Number.isFinite(candidate)
      ? candidate
      : fallback;
  return {
    bestScore: Math.max(0, Math.floor(finite(value.bestScore, 0))),
    tutorialComplete: bool(value.tutorialComplete, false),
    music: bool(value.music, true),
    effects: bool(value.effects, true),
    haptics: bool(value.haptics, true),
    muteAll: bool(value.muteAll, false),
    reducedMotion: bool(value.reducedMotion, false),
    sensitivity: Math.min(1.6, Math.max(0.6, finite(value.sensitivity, 1))),
    tiltSensitivity: Math.min(
      1.6,
      Math.max(0.6, finite(value.tiltSensitivity, 1)),
    ),
    tiltEnabled: bool(value.tiltEnabled, false),
    tiltCenter: Math.min(45, Math.max(-45, finite(value.tiltCenter, 0))),
  };
}

export class PreferenceStore {
  private memory = { ...DEFAULT_PREFERENCES };

  constructor(
    private readonly storage: Pick<
      Storage,
      "getItem" | "setItem"
    > | null = availableStorage(),
  ) {}

  load(): Preferences {
    try {
      const saved = this.storage?.getItem(STORAGE_KEY);
      this.memory = saved
        ? sanitize(JSON.parse(saved) as unknown)
        : { ...DEFAULT_PREFERENCES };
    } catch {
      this.memory = { ...DEFAULT_PREFERENCES };
    }
    return { ...this.memory };
  }

  save(next: Preferences): Preferences {
    this.memory = sanitize(next);
    try {
      this.storage?.setItem(STORAGE_KEY, JSON.stringify(this.memory));
    } catch {
      // Memory persistence remains available when storage is denied or full.
    }
    return { ...this.memory };
  }

  update(patch: Partial<Preferences>): Preferences {
    return this.save({ ...this.memory, ...patch });
  }
}

const finiteInteger = (value: unknown, fallback = 0): number =>
  typeof value === "number" && Number.isFinite(value)
    ? Math.max(0, Math.floor(value))
    : fallback;

function sanitizeLevelRecords(value: unknown): Record<string, LevelRecord> {
  if (!value || typeof value !== "object") return {};
  const records: Record<string, LevelRecord> = {};
  for (const [key, raw] of Object.entries(value)) {
    const id = Number(key);
    if (
      !Number.isInteger(id) ||
      id < 1 ||
      id > 10 ||
      !raw ||
      typeof raw !== "object"
    )
      continue;
    const record = raw as Partial<LevelRecord>;
    records[key] = {
      bestScore: finiteInteger(record.bestScore),
      stars: Math.min(3, finiteInteger(record.stars)),
      completions: finiteInteger(record.completions),
    };
  }
  return records;
}

function sanitizeRecentRuns(value: unknown): RecentRun[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(
      (raw): raw is Record<string, unknown> =>
        Boolean(raw) && typeof raw === "object",
    )
    .map((raw, index): RecentRun => {
      const mode = raw.mode === "campaign" ? "campaign" : "quick";
      const result = raw.result === "victory" ? "victory" : "defeat";
      const weapon =
        raw.weapon === "bubble" || raw.weapon === "plunger"
          ? raw.weapon
          : "poop";
      const levelId =
        mode === "campaign"
          ? Math.min(10, Math.max(1, finiteInteger(raw.levelId, 1)))
          : null;
      return {
        id:
          typeof raw.id === "string"
            ? raw.id.slice(0, 100)
            : `recovered-${index}`,
        mode,
        levelId,
        levelName:
          typeof raw.levelName === "string"
            ? raw.levelName.slice(0, 40)
            : mode === "quick"
              ? "Quick Play"
              : `Level ${levelId}`,
        score: finiteInteger(raw.score),
        clogs: finiteInteger(raw.clogs),
        stars: Math.min(3, finiteInteger(raw.stars)),
        weapon,
        result,
        completedAt:
          typeof raw.completedAt === "string"
            ? raw.completedAt.slice(0, 40)
            : new Date(0).toISOString(),
      };
    })
    .slice(0, MAX_RECENT_RUNS);
}

export function sanitizeProgress(
  raw: unknown,
  legacyBestScore = 0,
): PlayerProgress {
  const fallback = createPlayerProgress(legacyBestScore);
  if (!raw || typeof raw !== "object") return fallback;
  const value = raw as Partial<PlayerProgress>;
  const campaignClogs = finiteInteger(value.campaignClogs);
  const unlockedWeapons = weaponsForClogs(campaignClogs);
  const selectedWeapon = value.selectedWeapon;
  const nickname =
    typeof value.nickname === "string"
      ? normalizeNickname(value.nickname)
      : null;
  const failureStreaks: Record<string, number> = {};
  if (value.failureStreaks && typeof value.failureStreaks === "object") {
    for (const [key, streak] of Object.entries(value.failureStreaks)) {
      const id = Number(key);
      if (Number.isInteger(id) && id >= 1 && id <= 10)
        failureStreaks[key] = Math.min(99, finiteInteger(streak));
    }
  }
  return {
    schemaVersion: PROGRESS_SCHEMA_VERSION,
    campaignVersion: 1,
    nickname,
    createdAt:
      typeof value.createdAt === "string"
        ? value.createdAt
        : fallback.createdAt,
    updatedAt:
      typeof value.updatedAt === "string"
        ? value.updatedAt
        : fallback.updatedAt,
    highestUnlockedLevel: Math.min(
      10,
      Math.max(1, finiteInteger(value.highestUnlockedLevel, 1)),
    ),
    levelRecords: sanitizeLevelRecords(value.levelRecords),
    campaignClogs,
    lifetimeClogs: finiteInteger(value.lifetimeClogs),
    quickPlayBest: Math.max(
      finiteInteger(value.quickPlayBest),
      finiteInteger(legacyBestScore),
    ),
    unlockedWeapons,
    selectedWeapon:
      selectedWeapon === "bubble" || selectedWeapon === "plunger"
        ? unlockedWeapons.includes(selectedWeapon)
          ? selectedWeapon
          : "poop"
        : "poop",
    campaignComplete: value.campaignComplete === true,
    cosmeticAccentUnlocked: value.cosmeticAccentUnlocked === true,
    failureStreaks,
    recentRuns: sanitizeRecentRuns(value.recentRuns),
  };
}

export class ProgressStore {
  private memory = createPlayerProgress();

  constructor(
    private readonly storage: Pick<
      Storage,
      "getItem" | "setItem"
    > | null = availableStorage(),
  ) {}

  load(legacyBestScore = 0): PlayerProgress {
    try {
      const saved = this.storage?.getItem(PROGRESS_KEY);
      this.memory = saved
        ? sanitizeProgress(JSON.parse(saved) as unknown, legacyBestScore)
        : createPlayerProgress(legacyBestScore);
    } catch {
      this.memory = createPlayerProgress(legacyBestScore);
    }
    return structuredClone(this.memory);
  }

  save(next: PlayerProgress): PlayerProgress {
    this.memory = sanitizeProgress(next, next.quickPlayBest);
    try {
      this.storage?.setItem(PROGRESS_KEY, JSON.stringify(this.memory));
    } catch {
      // The current page keeps working from memory if storage is unavailable.
    }
    return structuredClone(this.memory);
  }

  clear(legacyBestScore = 0): PlayerProgress {
    return this.save(createPlayerProgress(legacyBestScore));
  }
}
